import type { LngLat, LngLatBounds, MapOptions } from 'maplibre-gl';
import { Map as NPMap, NPMapOptions } from '@npmap/base';
import { style } from './style';
import readParams from './readParams';
import type { CentralBounds, wzdxFeed } from './types';
import MergeFeatures from './mergeFeatures';
import type { FeatureCollection } from 'geojson';

interface AlertsMapParams {
    code?: string;
}

interface AlertsMapResponse {
    center: LngLat;
    bounds?: LngLatBounds;
    data?: wzdxFeed;
}

const blankGeoJson: FeatureCollection = {
    'type': 'FeatureCollection',
    'features': []
};


export default class AlertsMap {
    private map: NPMap | null = null;
    private static readonly apiKey = 'CfJDEBe7xKJ8v6xZOMkh7AaUGF70dBe3';
    private static readonly apiUrl = 'https://central.nps.gov';
    private static readonly defaultCenter = new NPMap.MapLibrary.LngLat(-96.02, 39.06);
    private static mergeFeatures = new MergeFeatures();

    private static readonly defaultMapOptions: NPMapOptions = {
        style: style({ type: 'FeatureCollection', features: [] }),
        center: AlertsMap.defaultCenter,
        zoom: 10,
        controls: {
            'navigation': {
                position: 'top-right',
                order: 2,
                showCompass: false,
                showZoom: true,
                visualizePitch: false
            }
        }
    };

    constructor(container: MapOptions['container'], params?: AlertsMapParams) {
        const urlParams = params ?? readParams(window.location.href);
        this.initializeMap(container, urlParams);
    }

    private async initializeMap(container: MapOptions['container'], params: AlertsMapParams): Promise<void> {
        const mapOptions = { ...AlertsMap.defaultMapOptions, container };

        try {
            if (!params.code) {
                this.loadMap(mapOptions);
                return;
            }

            const response = await this.fetchAlerts(params.code);
            if (!response) {
                this.loadMap(mapOptions);
                return;
            }

            const { center, bounds, data } = response;
            const updatedOptions = {
                ...mapOptions,
                style: style(data ?? blankGeoJson),
                center
            };

            const map = this.loadMap(updatedOptions);
            if (bounds) {
                map.fitBounds(bounds, { padding: 20 });
            }
        } catch (error) {
            console.error('Failed to initialize map:', error);
            this.loadMap(mapOptions);
        }
    }

    private loadMap(mapOptions: NPMapOptions): NPMap {
        if (this.map) {
            this.map.updateOptions(mapOptions, this.map.originalOptions);
            return this.map;
        }

        this.setupTableStyles();
        this.map = new NPMap(mapOptions);
        this.configureMapBehavior();

        return this.map;
    }

    private setupTableStyles(): void {
        const style = document.createElement('style');
        style.innerText = `.maplibregl-popup-content table {font-size: 12px;}`;
        document.body.appendChild(style);
    }

    private configureMapBehavior(): void {
        if (!this.map) return;

        this.map.dragRotate.disable();
        this.map.touchZoomRotate.disableRotation();

        this.map.once('load', () => {
            if (this.map) {
                this.map.setPaintProperty('RoadAlerts', 'line-opacity', 1);
            }
        });
    }

    private async fetchAlerts(code: string): Promise<AlertsMapResponse | undefined> {
        const { LngLat, LngLatBounds } = NPMap.MapLibrary;
        const qs = new URLSearchParams({
            apikey: AlertsMap.apiKey,
            code: code.toUpperCase()
        });

        const fetchParams: RequestInit = {
            method: 'GET',
            mode: 'cors',
            redirect: 'follow'
        };

        try {
            const [boundsRaw, dataRaw] = await Promise.all([
                fetch(`${AlertsMap.apiUrl}/data/api/v1/boundary/boundingbox/min?${qs.toString()}`, fetchParams),
                fetch(`${AlertsMap.apiUrl}/data/api/v1/wzdx/feeds?${new URLSearchParams([...Array.from(qs.entries()), ['format', 'geojson']])}`, fetchParams)
            ]);

            if (!boundsRaw.ok || !dataRaw.ok) {
                throw new Error('Failed to fetch data from API');
            }

            const boundsObj = await boundsRaw.json() as CentralBounds;
            const rawFeed = await dataRaw.json() as wzdxFeed;

            const bounds = new LngLatBounds(
                new LngLat(boundsObj[0].x, boundsObj[0].y),
                new LngLat(boundsObj[1].x, boundsObj[1].y)
            );

            const center = new LngLat(
                (boundsObj[0].x + boundsObj[1].x) / 2,
                (boundsObj[0].y + boundsObj[1].y) / 2
            );

            return {
                center,
                bounds,
                data: MergeFeatures.mergeFeatures(rawFeed)
            };
        } catch (error) {
            console.error('Error loading live data:', error);
            throw error; // Re-throw to handle in calling code
        }
    }
}